import type { ReactNode } from "react";
import type { FieldValues, UseFormMethods } from "react-hook-form";
import { Controller } from "react-hook-form";
import { LabeledTextField } from "@common/LabeledTextField/LabeledTextField";
import { useLabeledDateTextField } from "@common/LabeledDateTextField/useLabeledDateTextField";

export interface ILabeledDateTextFieldProps<TFieldValues extends FieldValues = FieldValues> {
    formMethods: UseFormMethods<TFieldValues>;
    name: keyof TFieldValues & string;
    labelText: string;
    disabled?: boolean;
    fullWidth?: boolean;
    helperText?: ReactNode;
    id?: string;
    isReadOnly?: boolean;
    isRequired?: boolean;
    placeholder?: string;
}

export const LabeledDateTextField = <TFieldValues extends FieldValues = FieldValues>(
    props: ILabeledDateTextFieldProps<TFieldValues>
) => {
    const {
        formMethods,
        name,
        labelText,
        disabled = false,
        fullWidth,
        helperText,
        id,
        isReadOnly = false,
        isRequired,
        placeholder = "DD/MM/YYYY",
    } = props;

    const { control, errors } = formMethods;
    const { onLabeledDateTextFieldChange, rules } = useLabeledDateTextField(isRequired);

    const fieldError = errors[name] as { message?: string } | undefined;

    return (
        <Controller
            control={control}
            name={name}
            render={(renderProps) => (
                <LabeledTextField
                    disabled={disabled}
                    error={Boolean(fieldError)}
                    fullWidth={fullWidth}
                    helperText={fieldError?.message ?? helperText}
                    id={id}
                    inputProps={{
                        inputMode: "numeric",
                        maxLength: 10,
                    }}
                    inputRef={renderProps.ref}
                    isReadOnly={isReadOnly}
                    isRequired={isRequired}
                    labelText={labelText}
                    name={renderProps.name}
                    placeholder={placeholder}
                    value={renderProps.value ?? ""}
                    onBlur={renderProps.onBlur}
                    onChange={(event) =>
                        onLabeledDateTextFieldChange(
                            event,
                            isReadOnly,
                            (renderProps.value as string) ?? "",
                            renderProps.onChange
                        )
                    }
                />
            )}
            rules={rules}
        />
    );
};
